import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  Pencil, Trash2, Phone, Mail, MessageCircle, UserPlus,
  GraduationCap, MapPin, Calendar, Clock, User as UserIcon, Building2,
  Megaphone, ExternalLink, ChevronDown, Check, Briefcase,
  StickyNote, CalendarClock,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { hasPermission } from "../../utils/permissions";
import { can } from "../../utils/access";
import { buildWhatsAppLink } from "../../utils/whatsappLink";
import {
  BackButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  AppButton, ConfirmModal, UserAvatar, DetailRow, DetailSection, Tabs, TabsList,
  TabsTrigger, TabsContent, EmptyState, Textarea,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import useClickToCall from "../../hooks/useClickToCall";
import useEnquiryStages from "../../hooks/useEnquiryStages";
import useEnquiryOwnership from "../../hooks/useEnquiryOwnership";
import CallStatusToast from "../Telephony/CallStatusToast";
import FollowUpModal from "./FollowUpModal";
import FollowUpDue from "./FollowUpDue";
import "./enquiry-view.scss";
import "./follow-up.scss";

// Statuses come from the institute's configured pipeline — see useEnquiryStages.

const SOURCE_LABELS = {
  manual: "Manual",
  website: "Website",
  embed_form: "Embed Form",
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  whatsapp: "WhatsApp",
  zoho_crm: "Zoho CRM",
  other: "Other",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const fmtRelative = (d) => {
  if (!d) return "";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

/* ── Status pill with inline dropdown ───────────────────────────────────── */
const StatusPill = ({ status, onChange, disabled, stages = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cls = (status || "Enquired").toLowerCase();

  // The `--enquired` / `--dropped` modifier classes only exist for the six
  // legacy names. A custom stage has no class, so it is coloured inline from its
  // configured colour instead — otherwise every institute-defined stage would
  // render as an unstyled grey pill.
  const stage = stages.find((s) => s.name === status);
  const isLegacyStyled = ["enquired", "confirmed", "dropped", "postponed", "closed", "converted"].includes(cls);
  const inlineStyle = !isLegacyStyled && stage?.color
    ? { background: `${stage.color}1f`, color: stage.color, borderColor: `${stage.color}59` }
    : undefined;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span className="eq-status-pill" ref={ref}>
      <button
        type="button"
        className={`eq-status-pill__btn eq-status-pill__btn--${cls}`}
        style={inlineStyle}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        {status || "Enquired"} {!disabled && <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="eq-status-pill__menu">
          {stages.map(({ name: s }) => (
            <button
              key={s}
              type="button"
              className={`eq-status-pill__option ${s === status ? "eq-status-pill__option--active" : ""}`}
              onClick={() => { setOpen(false); if (s !== status) onChange(s); }}
            >
              {s === status && <Check size={12} style={{ marginRight: 4 }} />} {s}
            </button>
          ))}
        </div>
      )}
    </span>
  );
};

/* ── Activity timeline (notes + status changes + creation) ──────────────── */
function adminDisplayName(a) {
  if (!a) return "";
  const first = a.firstName || "";
  const last = a.lastName || "";
  const full = `${first} ${last}`.trim();
  return full || a.fullName || a.name || (a.email ? a.email.split("@")[0] : "Admin");
}

function buildActivity(enquiry, activities = []) {
  if (!enquiry) return [];
  const events = [];

  // Created
  events.push({
    id: "created",
    type: "created",
    title: enquiry.leadSource && enquiry.leadSource !== "manual"
      ? `Lead received from ${SOURCE_LABELS[enquiry.leadSource] || enquiry.leadSource}`
      : "Enquiry created",
    actor: enquiry.createdBy || (enquiry.leadSource === "manual" ? "Admin" : "System"),
    at: enquiry.createdAt,
  });

  /* ── Assignment ──────────────────────────────────────────────────────────
     ⚠️ This synthetic row is a FALLBACK, not the history.

     `assignedTo` + `assignedAt` are a current-state pair: a reassignment
     overwrites both, so a row built from them shows exactly one handover — the
     latest — and rewrites itself to the new person on every change. Everyone
     who held the lead before today is simply gone from it.

     The real history is `type: 'assignment'` rows on the server timeline, one
     per change, written by `enquiryAssignmentService`. Those only exist from the
     day that shipped, so this row still stands in for enquiries assigned before
     it — dropping it outright would have blanked the assignment line on every
     historical lead. It disappears the moment there is a real one to show. */
  const hasAssignmentHistory = activities.some((a) => a.type === "assignment");
  if (enquiry.assignedAt && !hasAssignmentHistory) {
    events.push({
      id: "assigned",
      type: "assign",
      title: `Assigned to ${adminDisplayName(enquiry.assignedTo) || "an admin"}`,
      at: enquiry.assignedAt,
    });
  }

  // Everything that *happened* now comes from GET /enquiries/:id/timeline, which
  // merges the Activity collection with whatever still lives in the legacy
  // followUpNotes array. The synthetic events above and below are derived from
  // fields on the enquiry itself, so they stay client-side.
  for (const a of activities) {
    const failed = a.type === "whatsapp"
      ? a.meta?.status === "failed"
      : a.type === "call" && ["failed", "no-answer", "busy", "missed"].includes(a.meta?.status);

    events.push({
      id: `activity-${a._id}`,
      // Only legacy array entries can be deleted through the notes endpoint;
      // Activity rows are an audit trail and have no delete route.
      noteId: a.isLegacyNote ? a._id : null,
      /* `assignment` maps onto the `assign` visual, which already exists and is
         what the fallback row above uses — an unmapped type would render with
         no marker at all, since the timeline colours its dot per type. */
      type: a.type === "assignment"
        ? "assign"
        : a.type === "follow_up" || a.type === "status_change"
        ? "status"
        : a.type,
      failed,
      title: a.actorName && a.type === "note" ? `Note by ${a.actorName}` : a.title,
      body: a.body,
      /* Who did it. The title of a note already ends in the author's name, and
         the ingestion worker's actor is a label rather than a person — both
         would read as a stutter on the meta line. */
      actor: a.type === "note" ? "" : a.actorName || "",
      at: a.occurredAt,
    });
  }

  // Conversion
  if (enquiry.convertedToStudent) {
    events.push({
      id: "converted",
      type: "status",
      title: "Converted to student",
      at: enquiry.convertedAt || enquiry.updatedAt,
    });
  }

  return events.sort((a, b) => new Date(b.at) - new Date(a.at));
}

/* ════════════════════════════════════════════════════════════════════════
   Main Page Component
   ════════════════════════════════════════════════════════════════════════ */
const EnquiryViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const { stages } = useEnquiryStages();

  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Action modals
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);

  // Inline note add
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null);

  // Assignee picker
  const [admins, setAdmins] = useState([]);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  // Why the lead is moving, in the words of whoever moves it. OPTIONAL: a
  // required reason on a dropdown is a field people type "x" into, and a
  // history of "x" is worse than a history of blanks.
  const [assignNote, setAssignNote] = useState("");
  const assignRef = useRef(null);

  // Enquiry Ownership Control: under it a regular admin owns this lead but may
  // neither hand it on nor delete it. Both are refused server-side; hiding the
  // controls is what stops the refusal being a surprise.
  const ownership = useEnquiryOwnership();
  const canEdit = hasPermission("add_edit_enquiries");
  const canConvert = can("enquiries.convert");
  const canAssign = ownership.canAssign && canEdit;
  const canDelete = ownership.canDelete && canEdit;

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const loadEnquiry = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get(`/enquiries/${id}`);
      setEnquiry(data.enquiry);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load enquiry");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // A finished call writes a `channel: 'call'` note server-side, so reloading is
  // what makes it appear on the Activity tab.
  const call = useClickToCall({ onLogged: () => { loadEnquiry(); loadActivities(); } });

  // ── Activity timeline ───────────────────────────────────────────────────
  // Server-merged: Activity collection + legacy followUpNotes, de-duplicated.
  const [activities, setActivities] = useState([]);

  const loadActivities = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/enquiries/${id}/timeline`);
      setActivities(data.activities || []);
    } catch {
      setActivities([]);   // non-critical — the rest of the page still renders
    }
  }, [id]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  // ── Follow-ups ──────────────────────────────────────────────────────────
  const [followUps, setFollowUps] = useState([]);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  // The soonest pending one is the only one worth putting in the header — the
  // rest are visible in the list screens.
  const pendingFollowUp = followUps.find((f) => f.status === "pending") || null;

  const loadFollowUps = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/follow-ups", {
        params: { enquiryId: id, scope: "all", status: "pending" },
      });
      setFollowUps(data.followUps || []);
    } catch {
      setFollowUps([]);   // non-critical — the rest of the page still works
    }
  }, [id]);

  useEffect(() => { loadFollowUps(); }, [loadFollowUps]);

  const completeFollowUp = async (followUp) => {
    setSavingFollowUp(true);
    try {
      await apiClient.patch(`/follow-ups/${followUp._id}`, { status: "done" });
      showToast("Follow-up marked done.");
      await Promise.all([loadFollowUps(), loadEnquiry(), loadActivities()]);
    } catch (e) {
      showToast(e?.response?.data?.message || "Could not update the follow-up.", "error");
    }
    setSavingFollowUp(false);
  };

  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admins/list");
      setAdmins(data.admins || data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadEnquiry();
    loadAdmins();
  }, [loadEnquiry, loadAdmins]);

  // Close assign menu on outside click
  useEffect(() => {
    const h = (e) => assignRef.current && !assignRef.current.contains(e.target) && setShowAssignMenu(false);
    if (showAssignMenu) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showAssignMenu]);

  // ── Status update ────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    try {
      const { data } = await apiClient.put(`/enquiries/${id}/status`, { status: newStatus });
      setEnquiry((e) => ({ ...e, ...data.enquiry }));
      // The server records a status_change activity, so the timeline has to be
      // re-read rather than patched locally.
      loadActivities();
      showToast(`Status changed to ${newStatus}`);
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to update status", "error");
    }
  };

  // ── Assignment ───────────────────────────────────────────────────────────
  const handleAssign = async (adminId) => {
    setShowAssignMenu(false);
    const note = assignNote.trim();
    try {
      const { data } = await apiClient.put(`/enquiries/${id}/assign`, {
        assignedTo: adminId || null,
        note,
      });
      setEnquiry((e) => ({ ...e, assignedTo: data.enquiry.assignedTo, assignedAt: data.enquiry.assignedAt }));
      setAssignNote("");
      /* ⚠️ The server writes an `assignment` activity for this, exactly as it
         does for a status change — so the timeline has to be re-read. Patching
         `assignedTo` locally is what made the old page look like it had one
         assignment: the card updated and the history never did. */
      loadActivities();
      showToast(adminId ? "Lead reassigned" : "Assignment cleared");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to assign", "error");
    }
  };

  // ── Add note ─────────────────────────────────────────────────────────────
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await apiClient.post(`/enquiries/${id}/notes`, {
        note: newNote.trim(),
        createdBy: user?.name || user?.fullName || "Admin",
      });
      setEnquiry((e) => ({ ...e, followUpNotes: data.enquiry?.followUpNotes || e.followUpNotes }));
      loadActivities();
      setNewNote("");
      showToast("Note added");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to add note", "error");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setConfirmDeleteNote(null);
    try {
      const { data } = await apiClient.delete(`/enquiries/${id}/notes/${noteId}`);
      setEnquiry((e) => ({ ...e, followUpNotes: data.enquiry?.followUpNotes || e.followUpNotes }));
      loadActivities();
      showToast("Note deleted");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to delete note", "error");
    }
  };

  // ── Convert ──────────────────────────────────────────────────────────────
  const handleConvert = async () => {
    setConfirmConvert(false);
    setConverting(true);
    try {
      await apiClient.post(`/enquiries/${id}/convert-to-student`);
      showToast("Converted to student. Redirecting…");
      setTimeout(() => navigate("/students"), 1200);
    } catch (e) {
      showToast(e?.response?.data?.message || "Conversion failed", "error");
      setConverting(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await apiClient.delete(`/enquiries/${id}`);
      showToast("Enquiry deleted");
      setTimeout(() => navigate("/enquiries"), 600);
    } catch (e) {
      showToast(e?.response?.data?.message || "Delete failed", "error");
      setDeleting(false);
    }
  };

  const activity = useMemo(() => buildActivity(enquiry, activities), [enquiry, activities]);

  // ── Loading / error states ───────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spinner animation="border" />
      </div>
    );
  }
  if (error || !enquiry) {
    return (
      <div className="eq-view-page">
        <BackButton onClick={() => navigate("/enquiries")} />
        <EmptyState
          icon={<UserIcon size={40} />}
          title={error || "Enquiry not found"}
          description="This lead may have been deleted or you don't have access to it."
        />
      </div>
    );
  }

  // ── Derived display values ───────────────────────────────────────────────
  const phone = enquiry.mobile;
  const phoneLink = phone ? `tel:${phone}` : null;
  const emailLink = enquiry.email ? `mailto:${enquiry.email}` : null;
  const waLink = buildWhatsAppLink(phone, `Hi ${enquiry.fullName?.split(" ")[0] || ""}, `);
  const sourceKey = enquiry.leadSource || "manual";
  const sourceLabel = SOURCE_LABELS[sourceKey] || sourceKey;
  const courseName = enquiry.course?.name || enquiry.otherCourse || "—";
  const branchName = enquiry.branchId?.name || "—";
  const assignee = enquiry.assignedTo;
  const assigneeName = adminDisplayName(assignee);

  const customFieldEntries = enquiry.customFields
    ? Object.entries(enquiry.customFields instanceof Map
        ? Object.fromEntries(enquiry.customFields)
        : enquiry.customFields)
    : [];

  return (
    <div className="eq-view-page">
      {/* ── Sticky Header ───────────────────────────────────────────────── */}
      <div className="eq-view-header">
        <BackButton onClick={() => navigate("/enquiries")} />
        <h1 className="eq-view-header__title">{enquiry.fullName || "Unnamed Lead"}</h1>
        <StatusPill status={enquiry.status} onChange={handleStatusChange} stages={stages} disabled={!canEdit} />
        <div className="eq-view-header__actions">
          {canEdit && (
            <AppButton variant="secondary" size="sm" onClick={() => navigate(`/enquiries/edit/${id}`)}>
              <Pencil size={14} /> Edit
            </AppButton>
          )}
          {canDelete && (
            <AppButton variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} /> Delete
            </AppButton>
          )}
        </div>
      </div>

      {/* ── 3-column grid ───────────────────────────────────────────────── */}
      <div className="eq-view-layout">
        {/* ── LEFT: Contact card + key details ──────────────────────────── */}
        <div>
          <AppCard>
            <AppCardContent>
              <div className="eq-contact-card">
                <UserAvatar name={enquiry.fullName || "?"} size="xl" bordered />
                <h3 className="eq-contact-card__name">{enquiry.fullName || "Unnamed"}</h3>
                {sourceKey !== "manual" && (
                  <span className={`eq-source-badge eq-source-badge--${sourceKey}`}>
                    <Megaphone size={10} /> {sourceLabel}
                  </span>
                )}
                {phoneLink && (
                  <a className="eq-contact-row" href={phoneLink}>
                    <Phone size={14} /> {phone}
                  </a>
                )}
                {emailLink && (
                  <a className="eq-contact-row" href={emailLink}>
                    <Mail size={14} /> {enquiry.email}
                  </a>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard style={{ marginTop: 12 }}>
            <AppCardHeader>
              <AppCardTitle><Briefcase size={14} /> Details</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <DetailSection>
                <DetailRow icon={GraduationCap} label="Course" value={courseName} />
                <DetailRow icon={Building2} label="Branch" value={branchName} />
                <DetailRow icon={UserIcon} label="Type" value={enquiry.type || "—"} />
                {enquiry.degree && <DetailRow icon={GraduationCap} label="Degree" value={enquiry.degree} />}
                {enquiry.passoutYear && <DetailRow icon={Calendar} label="Passout Year" value={enquiry.passoutYear} />}
                {enquiry.experience && <DetailRow icon={Clock} label="Experience" value={enquiry.experience} />}
                {(enquiry.city || enquiry.address) && (
                  <DetailRow icon={MapPin} label="Location" value={[enquiry.city, enquiry.address].filter(Boolean).join(", ")} />
                )}
                <DetailRow icon={Calendar} label="Created" value={fmtDate(enquiry.createdAt)} />
                {enquiry.updatedAt && <DetailRow icon={Clock} label="Last update" value={fmtRelative(enquiry.updatedAt)} />}
              </DetailSection>
            </AppCardContent>
          </AppCard>
        </div>

        {/* ── CENTER: Tabs (Activity / Source / Zoho) ───────────────────── */}
        <div>
          <AppCard>
            <AppCardContent>
              <Tabs defaultValue="activity">
                <TabsList>
                  <TabsTrigger value="activity">
                    <StickyNote size={14} /> Activity
                    {activity.length > 0 && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-tertiary, #94a3b8)" }}>({activity.length})</span>
                    )}
                  </TabsTrigger>
                  {(sourceKey !== "manual" || customFieldEntries.length > 0) && (
                    <TabsTrigger value="source">
                      <Megaphone size={14} /> Source &amp; Campaign
                    </TabsTrigger>
                  )}
                  {enquiry.zohoLeadId && (
                    <TabsTrigger value="zoho">
                      <ExternalLink size={14} /> Zoho CRM
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* ── Activity Tab ────────────────────────────────────── */}
                <TabsContent value="activity">
                  <div style={{ paddingTop: 12 }}>
                    {canEdit && (
                      <div className="eq-note-add">
                        <textarea
                          rows={2}
                          placeholder="Add a follow-up note…"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                        />
                        <div className="eq-note-add__footer">
                          <span className="eq-note-add__hint">Press Ctrl+Enter to save</span>
                          <AppButton size="sm" onClick={handleAddNote} disabled={!newNote.trim() || addingNote}>
                            {addingNote ? "Adding…" : "Add Note"}
                          </AppButton>
                        </div>
                      </div>
                    )}

                    {activity.length === 0 ? (
                      <EmptyState
                        icon={<StickyNote size={32} />}
                        title="No activity yet"
                        description="Add a follow-up note to start tracking interactions."
                      />
                    ) : (
                      <div className="eq-timeline">
                        {activity.map((ev) => (
                          <div
                            key={ev.id}
                            className={`eq-timeline-item eq-timeline-item--${ev.type}${ev.failed ? " is-failed" : ""}`}
                          >
                            <div className="eq-timeline-item__title">
                              {ev.title}
                              {/* Call entries are deliberately not deletable: they
                                  record a real event, and removing one would let a
                                  replayed provider webhook re-create it. */}
                              {canEdit && (ev.type === "note" || ev.type === "whatsapp") && ev.noteId && (
                                <button
                                  className="eq-timeline-item__delete"
                                  title="Delete note"
                                  onClick={() => setConfirmDeleteNote(ev.noteId)}
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                            <div className="eq-timeline-item__meta">
                              {fmtRelative(ev.at)} &middot; {fmtDate(ev.at)}
                              {/* `actor` has been set on the "created" event
                                  since this page was written and rendered
                                  nowhere. An assignment history that cannot say
                                  who moved the lead is half a history. */}
                              {ev.actor && <> &middot; by {ev.actor}</>}
                            </div>
                            {ev.body && <div className="eq-timeline-item__body">{ev.body}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* ── Source Tab ──────────────────────────────────────── */}
                {(sourceKey !== "manual" || customFieldEntries.length > 0) && (
                  <TabsContent value="source">
                    <div style={{ paddingTop: 12 }}>
                      <DetailSection title="Source">
                        <DetailRow label="Source" value={
                          <span className={`eq-source-badge eq-source-badge--${sourceKey}`}>{sourceLabel}</span>
                        }/>
                        {enquiry.externalLeadId && <DetailRow label="External Lead ID" value={<code>{enquiry.externalLeadId}</code>} />}
                      </DetailSection>

                      {enquiry.campaign && Object.values(enquiry.campaign).some(Boolean) && (
                        <DetailSection title="Campaign">
                          {enquiry.campaign.name && <DetailRow label="Name" value={enquiry.campaign.name} />}
                          {enquiry.campaign.id && <DetailRow label="Campaign ID" value={<code>{enquiry.campaign.id}</code>} />}
                          {enquiry.campaign.adGroupName && <DetailRow label="Ad Group" value={enquiry.campaign.adGroupName} />}
                          {enquiry.campaign.adGroupId && <DetailRow label="Ad Group ID" value={<code>{enquiry.campaign.adGroupId}</code>} />}
                          {enquiry.campaign.formId && <DetailRow label="Form ID" value={<code>{enquiry.campaign.formId}</code>} />}
                          {enquiry.campaign.creativeId && <DetailRow label="Creative ID" value={<code>{enquiry.campaign.creativeId}</code>} />}
                        </DetailSection>
                      )}

                      {enquiry.utm && Object.values(enquiry.utm).some(Boolean) && (
                        <DetailSection title="UTM Parameters">
                          {enquiry.utm.source && <DetailRow label="Source" value={enquiry.utm.source} />}
                          {enquiry.utm.medium && <DetailRow label="Medium" value={enquiry.utm.medium} />}
                          {enquiry.utm.campaign && <DetailRow label="Campaign" value={enquiry.utm.campaign} />}
                          {enquiry.utm.term && <DetailRow label="Term" value={enquiry.utm.term} />}
                          {enquiry.utm.content && <DetailRow label="Content" value={enquiry.utm.content} />}
                        </DetailSection>
                      )}

                      {customFieldEntries.length > 0 && (
                        <>
                          <h4 style={{ fontSize: 13, color: "var(--text-secondary, #475569)", margin: "16px 0 8px" }}>Additional Fields</h4>
                          <div className="eq-custom-fields-grid">
                            {customFieldEntries.map(([k, v]) => (
                              <div className="eq-custom-field" key={k}>
                                <span className="eq-custom-field__label">{k.replace(/_/g, " ")}</span>
                                <span className="eq-custom-field__value">
                                  {v === null || v === undefined
                                    ? "—"
                                    : typeof v === "object"
                                      ? JSON.stringify(v)
                                      : String(v)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>
                )}

                {/* ── Zoho Tab ────────────────────────────────────────── */}
                {enquiry.zohoLeadId && (
                  <TabsContent value="zoho">
                    <div style={{ paddingTop: 12 }}>
                      <DetailSection>
                        <DetailRow label="Zoho Lead ID" value={<code>{enquiry.zohoLeadId}</code>} />
                        <DetailRow label="Sync status" value={enquiry.zohoSyncStatus || "Not synced"} />
                        {enquiry.zohoLastSyncedAt && (
                          <DetailRow label="Last synced" value={fmtDate(enquiry.zohoLastSyncedAt)} />
                        )}
                        {enquiry.zohoSyncError && (
                          <DetailRow label="Last error" value={<span style={{ color: "#ef4444" }}>{enquiry.zohoSyncError}</span>} />
                        )}
                      </DetailSection>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </AppCardContent>
          </AppCard>
        </div>

        {/* ── RIGHT: Follow-up + Quick actions + Assignment ──────────────── */}
        <div>
          {/* Placed above Quick Actions deliberately: "what do I owe this person
              next" is the first thing a counsellor opening an enquiry needs. */}
          <AppCard className="eq-followup-card">
            <AppCardHeader>
              <AppCardTitle>Next follow-up</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              {pendingFollowUp ? (
                <div className="eq-followup">
                  <div className="eq-followup__head">
                    <FollowUpDue dueAt={pendingFollowUp.dueAt} />
                    <span className="eq-followup__type">{pendingFollowUp.type}</span>
                  </div>
                  {pendingFollowUp.note && (
                    <p className="eq-followup__note">{pendingFollowUp.note}</p>
                  )}
                  <p className="eq-followup__meta">
                    {new Date(pendingFollowUp.dueAt).toLocaleString("en-IN", {
                      dateStyle: "medium", timeStyle: "short",
                    })}
                    {pendingFollowUp.assignedToName ? ` · ${pendingFollowUp.assignedToName}` : ""}
                  </p>
                  {canEdit && (
                    <div className="eq-followup__actions">
                      <AppButton size="sm" onClick={() => completeFollowUp(pendingFollowUp)} disabled={savingFollowUp}>
                        {savingFollowUp ? <Spinner animation="border" size="sm" /> : <><Check size={13} /> Mark done</>}
                      </AppButton>
                      <AppButton size="sm" variant="secondary" onClick={() => setRescheduleTarget(pendingFollowUp)}>
                        Reschedule
                      </AppButton>
                    </div>
                  )}
                </div>
              ) : (
                <div className="eq-followup eq-followup--empty">
                  <p className="eq-followup__none">Nothing scheduled.</p>
                  {canEdit && (
                    <AppButton size="sm" variant="secondary" onClick={() => setShowFollowUpModal(true)}>
                      <CalendarClock size={13} /> Schedule follow-up
                    </AppButton>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Quick Actions</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="eq-quick-actions">
                {/* With telephony configured this places a two-leg call through
                    the institute's own provider. Without it, the plain tel: link
                    stays exactly as it was. */}
                {call.enabled && phone ? (
                  <button type="button" className="eq-quick-action eq-quick-action--call"
                          disabled={call.calling}
                          onClick={() => call.placeCall({ enquiryId: id })}>
                    <Phone size={14} /> {call.calling ? "Calling…" : "Call"}
                  </button>
                ) : (
                  <a className="eq-quick-action eq-quick-action--call" href={phoneLink || undefined}
                     aria-disabled={!phoneLink} onClick={(e) => !phoneLink && e.preventDefault()}>
                    <Phone size={14} /> Call
                  </a>
                )}
                <a className="eq-quick-action eq-quick-action--email" href={emailLink || undefined}
                   aria-disabled={!emailLink} onClick={(e) => !emailLink && e.preventDefault()}>
                  <Mail size={14} /> Email
                </a>
                <a className="eq-quick-action eq-quick-action--whatsapp eq-quick-action--full"
                   href={waLink || undefined} target="_blank" rel="noreferrer"
                   aria-disabled={!waLink} onClick={(e) => !waLink && e.preventDefault()}>
                  <MessageCircle size={14} /> WhatsApp
                </a>
                {canConvert && enquiry.status !== "Converted" && (
                  <button
                    type="button"
                    className="eq-quick-action eq-quick-action--convert eq-quick-action--full"
                    onClick={() => setConfirmConvert(true)}
                    disabled={converting}
                  >
                    <UserPlus size={14} /> {converting ? "Converting…" : "Convert to Student"}
                  </button>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard style={{ marginTop: 12 }} className="eq-assign-card" overflowVisible>
            <AppCardHeader>
              <AppCardTitle>Assigned to</AppCardTitle>
            </AppCardHeader>
            <AppCardContent overflowVisible>
              <div ref={assignRef} style={{ position: "relative" }}>
                {/* Without the right to reassign this stays a read-out of the
                    owner, not a dead dropdown: same card, no chevron, no menu. */}
                <button
                  type="button"
                  className="eq-quick-action eq-quick-action--full"
                  onClick={() => canAssign && setShowAssignMenu((v) => !v)}
                  disabled={!canAssign}
                  style={{
                    justifyContent: "flex-start",
                    gap: 10,
                    ...(canAssign ? null : { cursor: "default", opacity: 1 }),
                  }}
                >
                  {assignee ? (
                    <>
                      <UserAvatar name={assigneeName} size="sm" />
                      <span style={{ flex: 1, textAlign: "left" }}>
                        <div style={{ fontWeight: 500 }}>{assigneeName}</div>
                        <div style={{ fontSize: 11, color: "var(--text-tertiary, #94a3b8)" }}>
                          since {fmtRelative(enquiry.assignedAt)}
                        </div>
                      </span>
                    </>
                  ) : (
                    <>
                      <UserIcon size={14} />
                      <span style={{ flex: 1, textAlign: "left", color: "var(--text-tertiary, #94a3b8)" }}>Unassigned</span>
                    </>
                  )}
                  {canAssign && <ChevronDown size={14} />}
                </button>
                {canAssign && showAssignMenu && (
                  <div className="eq-assign-menu">
                    {/* Typed BEFORE the name is picked, because picking the name
                        is what submits — one interaction, and the field is
                        inside `assignRef` so typing cannot close the menu. */}
                    <div className="eq-assign-note">
                      <label htmlFor="eq-assign-note">Reason <span>(optional)</span></label>
                      {/* The library control, not a bare <textarea>: one spec,
                          one focus ring, and `lint:components --check
                          rawcontrol` is a ratchet that may not go up. */}
                      <Textarea
                        id="eq-assign-note"
                        size="sm"
                        rows={2}
                        value={assignNote}
                        onChange={(ev) => setAssignNote(ev.target.value)}
                        placeholder="Why is this moving? Saved with the handover."
                        maxLength={500}
                      />
                    </div>
                    {assignee && (
                      <button
                        className="eq-assign-option eq-assign-option--danger"
                        onClick={() => handleAssign(null)}
                      >
                        Unassign
                      </button>
                    )}
                    {admins.length === 0 && (
                      <div className="eq-assign-empty">No admins available</div>
                    )}
                    {admins.map((a) => {
                      const isActive = assignee?._id === a._id;
                      const displayName = adminDisplayName(a);
                      const showEmail = a.email && a.email !== displayName;
                      return (
                        <button
                          key={a._id}
                          className={`eq-assign-option ${isActive ? "eq-assign-option--active" : ""}`}
                          onClick={() => handleAssign(a._id)}
                          title={a.email || displayName}
                        >
                          <UserAvatar name={displayName} size="sm" />
                          <span className="eq-assign-option__text">
                            <span className="eq-assign-option__name">{displayName}</span>
                            {showEmail && (
                              <span className="eq-assign-option__meta">{a.email}</span>
                            )}
                          </span>
                          {isActive && <Check size={14} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          {enquiry.referenceFrom && (
            <AppCard style={{ marginTop: 12 }}>
              <AppCardHeader>
                <AppCardTitle>Reference</AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <DetailSection>
                  <DetailRow label="From" value={enquiry.referenceFrom} />
                  {enquiry.referenceNumber && <DetailRow label="Mobile" value={enquiry.referenceNumber} />}
                </DetailSection>
              </AppCardContent>
            </AppCard>
          )}
        </div>
      </div>

      {/* ── Confirm modals ──────────────────────────────────────────────── */}
      <ConfirmModal
        show={confirmDelete}
        title="Delete enquiry?"
        message="This action cannot be undone. All notes and history will be lost."
        confirmText="Delete"
        confirmVariant="danger"
        onHide={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
      <ConfirmModal
        show={confirmConvert}
        title="Convert to Student?"
        message={`Create a student record for ${enquiry.fullName} and mark this enquiry as converted? You can adjust the batch and fees after conversion.`}
        confirmText="Convert"
        onHide={() => setConfirmConvert(false)}
        onConfirm={handleConvert}
      />
      <ConfirmModal
        show={!!confirmDeleteNote}
        title="Delete note?"
        message="This note will be permanently removed."
        confirmText="Delete"
        confirmVariant="danger"
        onHide={() => setConfirmDeleteNote(null)}
        onConfirm={() => handleDeleteNote(confirmDeleteNote)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <CallStatusToast call={call.activeCall} error={call.error} onDismiss={call.dismiss} />

      <FollowUpModal
        show={showFollowUpModal || !!rescheduleTarget}
        onHide={() => { setShowFollowUpModal(false); setRescheduleTarget(null); }}
        enquiry={enquiry}
        followUp={rescheduleTarget}
        onSaved={() => { loadFollowUps(); loadEnquiry(); loadActivities(); }}
      />
    </div>
  );
};

export default EnquiryViewPage;
